// ────────────────────────────────────────────────────────────────────
// InspectionDetailScreen — Level 5: Inspection detail
//
// Route: /inspections/:inspectionId
// Shows the readings captured in this inspection (native table, RLS-safe)
// and lets the customer download the raw source data it was imported from.
//
// (Replaced an iframe embed of the legacy C-Scan report.html: it 401'd
// cross-origin — showed a login page — and there is no C-Scan/decal data in
// this dataset anyway, only UTM thickness + GVI. The thickness *is* the data,
// so we render it natively and offer the original Excel as the raw download.)
// ────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, Share2 } from "lucide-react";
import { apiClient, errMsg } from "@/lib/api";

const CLOCK = ["12", "3", "6", "9"];
// Readings label the 4 quadrant positions by degrees (0/90/180/270) or clock
// (12/3/6/9) — same points (0°≡12, 90°≡3, 180°≡6, 270°≡9). Normalize so the columns
// populate regardless of the source's labelling (matches the staff Review Studio);
// this is the inspection EVIDENCE record, so per-position detail stays (unlike the
// asset-summary CML table, which is curated to the client decision view).
const POS_TO_CLOCK: Record<string, string> = {
  "0": "12", "0°": "12", "360": "12", "360°": "12",
  "90": "3", "90°": "3", "180": "6", "180°": "6", "270": "9", "270°": "9",
};
const toClock = (p: string): string => POS_TO_CLOCK[String(p).trim()] ?? p;
function parsePositions(json: string): { pos: string; mm: number }[] {
  try { const a = JSON.parse(json); return Array.isArray(a) ? a : []; } catch { return []; }
}

export default function InspectionDetailScreen(): JSX.Element {
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const id = Number(inspectionId);
  const [dl, setDl] = useState<{ state: "idle" | "loading" | "error"; msg?: string }>({ state: "idle" });

  const inspQ = useQuery({
    queryKey: ["inspection", id],
    queryFn: () => apiClient.inspection(id),
    enabled: !!id,
  });
  const insp = inspQ.data;

  const measQ = useQuery({
    queryKey: ["measurements", { inspection_id: id }],
    queryFn: () => apiClient.measurements({ inspection_id: id }),
    enabled: !!id,
  });
  const readings = measQ.data ?? [];

  async function downloadRaw(): Promise<void> {
    setDl({ state: "loading" });
    try {
      const { blob, filename } = await apiClient.inspectionRawExcel(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename || `${insp?.doc_no || `INSP-${id}`}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setDl({ state: "idle" });
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const msg = status === 404 ? "no raw source file is linked to this inspection"
                : status === 503 ? "the raw-source archive is offline on this server"
                : errMsg(e);
      setDl({ state: "error", msg });
    }
  }

  // Fetch parent asset for breadcrumb (deferred — depends on inspection)
  const assetQ = useQuery({
    queryKey: ["asset", insp?.asset_id],
    queryFn: () => apiClient.asset(insp!.asset_id!),
    enabled: !!insp?.asset_id,
  });
  const asset = assetQ.data;

  if (inspQ.isPending) return <CenterStatus>Loading inspection…</CenterStatus>;
  if (inspQ.isError || !insp) {
    return (
      <CenterStatus tone="err">
        Inspection not found.{" "}
        <Link to="/atlas" className="underline" style={{ color: "var(--accent-deep)" }}>Back to Atlas</Link>
      </CenterStatus>
    );
  }

  const date = insp.inspection_date || insp.upload_date.slice(0, 10);
  const docLabel = insp.doc_no || `INSP-${insp.id}`;

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden" style={{ background: "var(--paper)" }}>

      {/* ── Breadcrumb ───────────────────────────────────────── */}
      <div className="px-6 py-2.5 border-b flex items-center justify-between"
           style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase flex items-center gap-2"
             style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
          <Link to="/atlas" className="hover:text-[var(--accent)] transition-colors">ATLAS</Link>
          {asset && <>
            <span className="opacity-40">/</span>
            <Link to={asset.plant_id ? `/sites/${asset.plant_id}` : "/atlas"}
                  className="hover:text-[var(--accent)] transition-colors">{asset.plant_code}</Link>
            <span className="opacity-40">/</span>
            <Link to={`/assets/${asset.id}`}
                  className="hover:text-[var(--accent)] transition-colors">{asset.tag}</Link>
          </>}
          <span className="opacity-40">/</span>
          <span style={{ color: "var(--ink-1)" }}>{docLabel}</span>
        </div>
        {insp.asset_id && (
          <Link to={`/assets/${insp.asset_id}`}
                className="font-mono uppercase flex items-center gap-1.5 hover:text-[var(--accent)] transition-colors"
                style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
            <ArrowLeft size={11} />
            BACK TO ASSET
          </Link>
        )}
      </div>

      {/* ── Inspection header ───────────────────────────────── */}
      <header className="px-8 py-4 border-b flex items-baseline justify-between gap-6"
              style={{ borderColor: "var(--hairline)" }}>
        <div className="min-w-0">
          <div className="font-mono uppercase mb-1"
               style={{ fontSize: 10.5, letterSpacing: ".24em", color: "var(--accent-deep)", fontWeight: 600 }}>
            / INSPECTION REPORT · {insp.technique}
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-mono"
                  style={{ fontSize: 22, letterSpacing: ".06em", color: "var(--accent-deep)", fontWeight: 700 }}>
              {docLabel}
            </span>
            <h1 className="font-sans font-medium m-0 truncate"
                style={{ fontSize: 22, color: "var(--ink-1)", letterSpacing: "-.01em" }}>
              {insp.project_name || insp.vessel_id || "—"}
            </h1>
          </div>
          <div className="flex items-center gap-4 mt-1.5 font-mono"
               style={{ fontSize: 11, color: "var(--ink-3)" }}>
            <span className="tabular-nums">{date}</span>
            {insp.inspector_name && (
              <>
                <span className="opacity-40">·</span>
                <span>Inspector · {insp.inspector_name}</span>
              </>
            )}
            <span className="opacity-40">·</span>
            <span>Status · <strong style={{ color: "var(--ink-1)" }}>{insp.status.toUpperCase()}</strong></span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 shrink-0">
          <ActionBtn icon={<Share2 size={11} />} label="SHARE"
                     onClick={() => shareLink(window.location.href)} />
          <ActionBtn icon={<Download size={11} />}
                     label={dl.state === "loading" ? "PREPARING…" : "DOWNLOAD RAW DATA"} primary
                     onClick={() => { if (dl.state !== "loading") downloadRaw(); }} />
        </div>
      </header>

      {/* ── Readings captured in this inspection (native) ───── */}
      <div className="flex-1 overflow-auto px-8 py-5">
        {dl.state === "error" && (
          <div className="mb-4 px-3 py-2.5 font-mono" style={{
            fontSize: 11, color: "var(--err)", border: "1px dashed var(--hairline)", background: "var(--paper-2)" }}>
            Raw source download unavailable — {dl.msg}
          </div>
        )}
        <div className="font-mono uppercase mb-3" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
          / READINGS · {measQ.isSuccess ? readings.length : "—"}
        </div>

        {measQ.isPending && <div className="font-mono text-xs" style={{ color: "var(--ink-3)" }}>Loading readings…</div>}
        {measQ.isSuccess && readings.length === 0 && (
          <div className="px-3 py-2.5 font-mono" style={{
            fontSize: 11, color: "var(--ink-3)", border: "1px dashed var(--hairline)", background: "var(--paper-2)" }}>
            No thickness readings captured in this inspection.
            {insp.technique === "GVI" && " (GVI walkdown — visual findings only.)"}
          </div>
        )}

        {readings.length > 0 && (
          <div className="border" style={{ borderColor: "var(--hairline)" }}>
            <div className="grid items-center gap-2 px-3 py-2 border-b font-mono uppercase"
                 style={{ gridTemplateColumns: "1.4fr repeat(4,1fr) 1fr 1.2fr 1fr", fontSize: 9,
                          letterSpacing: ".1em", color: "var(--ink-4)", borderColor: "var(--hairline)", background: "var(--paper-2)" }}>
              <span>CML</span>
              {CLOCK.map((p) => <span key={p} className="text-right">{p}·oc</span>)}
              <span className="text-right">MIN</span>
              <span className="text-right">gauge</span>
              <span className="text-right">access</span>
            </div>
            {readings.map((m) => {
              const pos = parsePositions(m.readings_json);
              const at = (p: string): string => { const x = pos.find((r) => toClock(r.pos) === p); return x && x.mm > 0 ? x.mm.toFixed(2) : "–"; };
              const min = m.representative_mm > 0 ? m.representative_mm.toFixed(2) : "–";
              return (
                <div key={m.id} className="grid items-center gap-2 px-3 py-1.5 border-b font-mono tabular-nums"
                     style={{ gridTemplateColumns: "1.4fr repeat(4,1fr) 1fr 1.2fr 1fr", fontSize: 11, borderColor: "var(--hairline)" }}>
                  <span style={{ color: "var(--ink-1)", fontWeight: 600 }}>{m.cml_no}</span>
                  {CLOCK.map((p) => <span key={p} className="text-right" style={{ color: "var(--ink-2)" }}>{at(p)}</span>)}
                  <span className="text-right" style={{ color: "var(--ink-1)", fontWeight: 700 }}>{min}</span>
                  <span className="text-right truncate" style={{ color: "var(--ink-3)" }}>{m.gauge || "–"}</span>
                  <span className="text-right uppercase" style={{ color: "var(--ink-4)", fontSize: 9 }}>{m.access_status || "–"}</span>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 font-mono" style={{ fontSize: 10, letterSpacing: ".04em", color: "var(--ink-4)" }}>
          Values in mm · MIN = governing (representative) reading · “DOWNLOAD RAW DATA” fetches the original source workbook this inspection was imported from.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Toolbar actions
// ─────────────────────────────────────────────────────────────────────
function shareLink(url: string): void {
  if (navigator.share) {
    navigator.share({ url, title: "Inspection report" }).catch(() => {});
  } else {
    navigator.clipboard?.writeText(url).then(
      () => alert("Link copied to clipboard."),
      () => prompt("Copy this link:", url),
    );
  }
}

// ─────────────────────────────────────────────────────────────────────
// ActionBtn — toolbar button styling
// ─────────────────────────────────────────────────────────────────────
function ActionBtn({
  icon, label, onClick, primary,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  primary?: boolean;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className="px-3 py-2 font-mono uppercase flex items-center gap-1.5 transition-colors"
      style={{
        fontSize: 10.5, letterSpacing: ".22em",
        color: primary ? "var(--ink-1)" : "var(--ink-3)",
        background: primary ? "var(--accent)" : "transparent",
        border: primary ? "1px solid var(--accent)" : "1px solid var(--hairline)",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        if (!primary) e.currentTarget.style.borderColor = "var(--ink-3)";
      }}
      onMouseLeave={(e) => {
        if (!primary) e.currentTarget.style.borderColor = "var(--hairline)";
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function CenterStatus({
  children, tone,
}: { children: React.ReactNode; tone?: "err" }): JSX.Element {
  return (
    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "var(--paper)" }}>
      <div className="font-mono uppercase"
           style={{
             fontSize: 12, letterSpacing: ".22em",
             color: tone === "err" ? "var(--err)" : "var(--ink-3)",
           }}>
        {children}
      </div>
    </div>
  );
}
